import React, { useState } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Plus, Wallet, CalendarRange, TrendingUp, CalendarClock } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { PinnableCard, ReportBackdrop } from '../components/crm/reportCards';
import {
  GLASS_TEXT,
  GLASS_FIELD,
  GLASS_TH,
  GLASS_ROW,
  GLASS_SECONDARY_BUTTON,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassBadge,
  GlassAccentButton,
} from '../components/glass/GlassUi';

const TechnologyReports = () => {
  const navigate = useNavigate();
  
  
  const reportData = {
    systemData: [
      { month: 'Jan', activeUsers: 85, avgSessionTime: 45, totalUsers: 120 },
      { month: 'Feb', activeUsers: 88, avgSessionTime: 48, totalUsers: 120 },
      { month: 'Mar', activeUsers: 92, avgSessionTime: 50, totalUsers: 120 },
      { month: 'Apr', activeUsers: 90, avgSessionTime: 52, totalUsers: 120 },
      { month: 'May', activeUsers: 95, avgSessionTime: 55, totalUsers: 120 },
      { month: 'Jun', activeUsers: 102, avgSessionTime: 58, totalUsers: 120 }
    ],
    apiCostsData: [
      { month: 'Jan', openai: 1250, stripe: 450, twilio: 320, google: 280 },
      { month: 'Feb', openai: 1380, stripe: 480, twilio: 340, google: 290 },
      { month: 'Mar', openai: 1520, stripe: 520, twilio: 360, google: 310 },
      { month: 'Apr', openai: 1680, stripe: 580, twilio: 380, google: 330 },
      { month: 'May', openai: 1850, stripe: 620, twilio: 400, google: 350 },
      { month: 'Jun', openai: 2100, stripe: 680, twilio: 420, google: 380 }
    ],
    automationData: [
      { month: 'Jan', coverage: 65, avgTurnaround: 14 },
      { month: 'Feb', coverage: 68, avgTurnaround: 12 },
      { month: 'Mar', coverage: 72, avgTurnaround: 10 },
      { month: 'Apr', coverage: 75, avgTurnaround: 9 },
      { month: 'May', coverage: 78, avgTurnaround: 8 },
      { month: 'Jun', coverage: 82, avgTurnaround: 7 }
    ],
    dataSegmentation: [
      { name: 'Client Data', value: 45, color: '#5AB9EA' },
      { name: 'Internal Data', value: 25, color: '#84CEEB' },
      { name: 'Analytics', value: 20, color: '#5680E9' },
      { name: 'Backup', value: 10, color: '#F8A055' }
    ],
    bugReportData: [
      { month: 'Jan', crmBugs: 15, portalBugs: 8 },
      { month: 'Feb', crmBugs: 12, portalBugs: 6 },
      { month: 'Mar', crmBugs: 10, portalBugs: 5 },
      { month: 'Apr', crmBugs: 8, portalBugs: 4 },
      { month: 'May', crmBugs: 6, portalBugs: 3 },
      { month: 'Jun', crmBugs: 5, portalBugs: 2 }
    ],
    performanceData: [
      { month: 'Jan', activeUsers: 1200, avgSessionTime: 15, pageViews: 8500 },
      { month: 'Feb', activeUsers: 1350, avgSessionTime: 18, pageViews: 9200 },
      { month: 'Mar', activeUsers: 1420, avgSessionTime: 20, pageViews: 9800 },
      { month: 'Apr', activeUsers: 1580, avgSessionTime: 22, pageViews: 10500 },
      { month: 'May', activeUsers: 1650, avgSessionTime: 25, pageViews: 11200 },
      { month: 'Jun', activeUsers: 1800, avgSessionTime: 28, pageViews: 12500 }
    ],
    projectData: [
      { project: 'Frontend', progress: 85, health: 'Good' },
      { project: 'Backend', progress: 92, health: 'Excellent' },
      { project: 'Mobile', progress: 78, health: 'Good' },
      { project: 'DevOps', progress: 88, health: 'Good' },
      { project: 'Security', progress: 95, health: 'Excellent' },
      { project: 'AI/ML', progress: 65, health: 'Fair' }
    ],
    kpiData: [
      { metric: 'System Uptime', target: 99.9, actual: 99.9, variance: 0 },
      { metric: 'Response Time', target: 150, actual: 120, variance: -20 },
      { metric: 'Bug Resolution', target: 95, actual: 94, variance: -1 },
    ],
    metrics: {
      overallUptime: 99.9,
      totalIncidents: 28,
      avgResponseTime: '120ms',
      securityScore: 92,
      codeQuality: 88,
      deploymentSuccess: 98.5,
      testCoverage: 85,
      technicalDebt: 12,
      clientLoginFrequency: 12.5,
      automationCoverage: 78,
      totalBugReports: 156,
      internalBugs: 89,
      clientBugs: 67,
      dataCollected: 12500
    },
    topProjects: [
      { title: 'Cloud Migration', impact: '40% cost reduction' },
      { title: 'CI/CD Pipeline', impact: '60% faster deployments' },
      { title: 'Security Audit', impact: '25% risk reduction' }
    ]
  };

  const [subscriptions, setSubscriptions] = useState([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [newSubscription, setNewSubscription] = useState({
    program: '',
    amount: '',
    period: 'Monthly',
    startDate: '',
    endDate: '',
    status: 'active',
    description: ''
  });
  const [editSubscription, setEditSubscription] = useState({
    program: '',
    amount: '',
    period: 'Monthly',
    startDate: '',
    endDate: '',
    status: 'active',
    description: ''
  });

  const handleAddSubscription = async (e) => {
    e.preventDefault();
    const amountNumber = Number(newSubscription.amount);
    if (!newSubscription.program || isNaN(amountNumber) || !newSubscription.startDate) return;
    await addDoc(collection(db, 'technologySubscriptions'), {
      program: newSubscription.program,
      amount: amountNumber,
      period: newSubscription.period,
      startDate: newSubscription.startDate,
      endDate: newSubscription.endDate || null,
      status: newSubscription.status,
      description: newSubscription.description,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    setNewSubscription({ program: '', amount: '', period: 'Monthly', startDate: '', endDate: '', status: 'active', description: '' });
    setIsAddOpen(false);
  };

  const openAdd = (asUpcoming = false) => {
    setNewSubscription({
      program: '',
      amount: '',
      period: 'Monthly',
      startDate: '',
      endDate: '',
      status: asUpcoming ? 'upcoming' : 'active',
      description: ''
    });
    setIsAddOpen(true);
  };

  // Realtime shared subscriptions
  React.useEffect(() => {
    const col = collection(db, 'technologySubscriptions');
    const qy = query(col, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(qy, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSubscriptions(list);
    });
    return () => unsub();
  }, []);

  const openEdit = (sub, idx) => {
    setSelectedId(sub.id || null);
    setEditSubscription({
      program: sub.program,
      amount: String(sub.amount),
      period: sub.period,
      startDate: sub.startDate,
      endDate: sub.endDate || '',
      status: sub.status,
      description: sub.description || ''
    });
    setIsEditOpen(true);
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    const amountNumber = Number(editSubscription.amount);
    if (!editSubscription.program || isNaN(amountNumber) || !editSubscription.startDate) return;
    await updateDoc(doc(db, 'technologySubscriptions', selectedId), {
      program: editSubscription.program,
      amount: amountNumber,
      period: editSubscription.period,
      startDate: editSubscription.startDate,
      endDate: editSubscription.endDate || null,
      status: editSubscription.status,
      description: editSubscription.description,
      updatedAt: serverTimestamp()
    });
    setIsEditOpen(false);
    setSelectedId(null);
  };

  const handleDeleteSubscription = async () => {
    if (!selectedId) return;
    await deleteDoc(doc(db, 'technologySubscriptions', selectedId));
    setIsEditOpen(false);
    setSelectedId(null);
  };

  // Calculate metrics
  const totalIncidents = reportData.performanceData.reduce((sum, item) => sum + item.incidents, 0);
  const avgUptime = reportData.performanceData.reduce((sum, item) => sum + item.uptime, 0) / reportData.performanceData.length;
  const avgResponseTime = reportData.performanceData.reduce((sum, item) => sum + item.responseTime, 0) / reportData.performanceData.length;
  const projectHealth = reportData.projectData.reduce((sum, item) => sum + (item.health === 'Excellent' ? 100 : item.health === 'Good' ? 80 : 60), 0) / reportData.projectData.length;

  

  const costPerMonth = reportData.apiCostsData.map(item => ({
    month: item.month,
    cost: (item.openai || 0) + (item.stripe || 0) + (item.twilio || 0) + (item.google || 0)
  }));

  const getMonthlyEquivalent = (sub) => {
    if (!sub || typeof sub.amount !== 'number') return 0;
    if (sub.period === 'Monthly') return sub.amount;
    if (sub.period === 'Quarterly') return sub.amount / 3;
    if (sub.period === 'Yearly') return sub.amount / 12;
    return sub.amount; // default fallback
  };

  const monthlySoftwareCost = subscriptions
    .filter(sub => sub.status !== 'inactive')
    .reduce((sum, sub) => sum + getMonthlyEquivalent(sub), 0);
  const annualSoftwareCost = monthlySoftwareCost * 12;

  const upcomingSubscriptions = subscriptions.filter(s => s.status === 'upcoming');
  const futureMonthlyAdd = upcomingSubscriptions.reduce((sum, sub) => sum + getMonthlyEquivalent(sub), 0);
  const futureMonthlyTotal = monthlySoftwareCost + futureMonthlyAdd;
  const futureAnnualTotal = futureMonthlyTotal * 12;

  const getQuarterLabel = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const q = Math.floor(d.getMonth() / 3) + 1;
    return `Q${q} ${d.getFullYear()}`;
  };

  const statusTone = (status) => (status === 'active'
    ? 'mint'
    : status === 'upcoming'
      ? 'blue'
      : status === 'inactive'
        ? 'neutral'
        : status === 'phasing out'
          ? 'amber'
          : 'red');

  const formatCost = (n) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  const stats = [
    { icon: Wallet, value: formatCost(monthlySoftwareCost), label: 'Monthly (Active)' },
    { icon: CalendarRange, value: formatCost(annualSoftwareCost), label: 'Annual (Active)' },
    { icon: TrendingUp, value: formatCost(futureMonthlyTotal), label: 'Future Monthly (with additions)', valueClassName: GLASS_TEXT.blue },
    { icon: CalendarClock, value: formatCost(futureAnnualTotal), label: 'Future Annual (with additions)', valueClassName: GLASS_TEXT.blue },
  ];

  const muted = 'text-gray-500 dark:text-white/50';
  const labelCls = `mb-1 block text-sm ${muted}`;

  const addButton = (onClick, label) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="grid h-10 w-10 place-items-center rounded-full bg-[#F80707] text-white shadow-[0_8px_24px_-10px_rgba(248,7,7,0.75)] transition hover:bg-[#C90505] active:scale-[0.98]"
    >
      <Plus className="h-5 w-5" />
    </button>
  );

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        onBack={() => navigate('/crm/reports')}
        backTitle="Back to reports"
        title={<>Technology<br />Reports</>}
        stats={stats}
      />

      <div className="space-y-4">
        {/* Subscriptions Table */}
        <PinnableCard id="active-subscriptions" title="Active Subscriptions" size="wide">
        <GlassCard className="p-5" delay={0}>
          <GlassCardHeader
            title="Active Subscriptions"
            right={addButton(() => openAdd(false), 'Add New Subscription')}
          />
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className={GLASS_TH}>Program</th>
                  <th className={GLASS_TH}>Amount</th>
                  <th className={GLASS_TH}>Period</th>
                  <th className={GLASS_TH}>Start Date</th>
                  <th className={GLASS_TH}>End Date</th>
                  <th className={GLASS_TH}>Status</th>
                </tr>
              </thead>
              <tbody className="text-gray-800 dark:text-white/[0.85]">
                {subscriptions.filter(s => s.status !== 'upcoming').map((sub, idx) => {
                  const rowColor = sub.status === 'phasing out'
                    ? 'bg-[#FFB547]/10 dark:bg-[#FFB547]/[0.06]'
                    : sub.status === 'need to kill'
                      ? 'bg-[#F80707]/[0.06] dark:bg-[#F80707]/[0.08]'
                      : sub.status === 'inactive'
                        ? 'bg-black/[0.02] dark:bg-white/[0.02]'
                        : '';
                  const dimText = sub.status === 'inactive' ? 'text-gray-400 dark:text-white/40' : '';
                  return (
                  <tr
                    key={idx}
                    onClick={() => openEdit(sub, idx)}
                    className={`${GLASS_ROW} ${rowColor} cursor-pointer`}
                  >
                    <td className={`py-3 pr-4 whitespace-nowrap font-medium ${dimText || 'text-gray-900 dark:text-white'}`}>{sub.program}</td>
                    <td className={`py-3 pr-4 whitespace-nowrap tabular-nums ${dimText}`}>{`$${sub.amount.toLocaleString()}`}</td>
                    <td className={`py-3 pr-4 whitespace-nowrap ${dimText}`}>{sub.period}</td>
                    <td className={`py-3 pr-4 whitespace-nowrap ${dimText}`}>{sub.startDate}</td>
                    <td className={`py-3 pr-4 whitespace-nowrap ${dimText}`}>{sub.endDate || '—'}</td>
                    <td className="py-3 whitespace-nowrap">
                      <GlassBadge tone={statusTone(sub.status)}>{sub.status}</GlassBadge>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </GlassCard>
        </PinnableCard>

        {/* Future Subscriptions Overview */}
        <PinnableCard id="future-subscriptions" title="Future Subscriptions" size="wide">
        <GlassCard className="p-5" delay={80}>
          <GlassCardHeader
            title="Future Subscriptions"
            right={addButton(() => openAdd(true), 'Add New Future Subscription')}
          />
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className={GLASS_TH}>Program</th>
                  <th className={GLASS_TH}>Amount</th>
                  <th className={GLASS_TH}>Period</th>
                  <th className={GLASS_TH}>Start Quarter</th>
                  <th className={GLASS_TH}>Description</th>
                </tr>
              </thead>
              <tbody className="text-gray-800 dark:text-white/[0.85]">
                {upcomingSubscriptions.map((sub, idx) => (
                  <tr
                    key={idx}
                    onClick={() => openEdit(sub, idx)}
                    className={`${GLASS_ROW} cursor-pointer`}
                  >
                    <td className="py-3 pr-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">{sub.program}</td>
                    <td className="py-3 pr-4 whitespace-nowrap tabular-nums">{`$${sub.amount.toLocaleString()}`}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{sub.period}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      <GlassBadge tone="blue">{getQuarterLabel(sub.startDate)}</GlassBadge>
                    </td>
                    <td className={`py-3 ${muted}`}>{sub.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
        </PinnableCard>
      </div>

      {/* Kept outside the glass cards: backdrop-filter would trap a fixed overlay inside them */}
      {isAddOpen && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={GLASS_MODAL_PANEL}>
            <h3 className="mb-4 text-center text-lg font-medium">Add Subscription</h3>
            <form onSubmit={handleAddSubscription} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={labelCls}>Program</label>
                <input
                  type="text"
                  className={GLASS_FIELD}
                  value={newSubscription.program}
                  onChange={(e) => setNewSubscription(s => ({ ...s, program: e.target.value }))}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Description</label>
                <textarea
                  className={GLASS_FIELD}
                  rows={3}
                  value={newSubscription.description}
                  onChange={(e) => setNewSubscription(s => ({ ...s, description: e.target.value }))}
                />
              </div>
              <div>
                <label className={labelCls}>Amount</label>
                <input
                  type="number"
                  className={GLASS_FIELD}
                  value={newSubscription.amount}
                  onChange={(e) => setNewSubscription(s => ({ ...s, amount: e.target.value }))}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className={labelCls}>Period</label>
                <select
                  className={GLASS_FIELD}
                  value={newSubscription.period}
                  onChange={(e) => setNewSubscription(s => ({ ...s, period: e.target.value }))}
                >
                  <option>Monthly</option>
                  <option>Quarterly</option>
                  <option>Yearly</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Start Date</label>
                <input
                  type="date"
                  className={GLASS_FIELD}
                  value={newSubscription.startDate}
                  onChange={(e) => setNewSubscription(s => ({ ...s, startDate: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>End Date</label>
                <input
                  type="date"
                  className={GLASS_FIELD}
                  value={newSubscription.endDate}
                  onChange={(e) => setNewSubscription(s => ({ ...s, endDate: e.target.value }))}
                />
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select
                  className={GLASS_FIELD}
                  value={newSubscription.status}
                  onChange={(e) => setNewSubscription(s => ({ ...s, status: e.target.value }))}
                >
                  <option value="active">active</option>
                  <option value="upcoming">upcoming</option>
                  <option value="inactive">inactive</option>
                  <option value="phasing out">phasing out</option>
                  <option value="need to kill">need to kill</option>
                </select>
              </div>
              <div className="md:col-span-2 mt-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsAddOpen(false)}
                  className={GLASS_SECONDARY_BUTTON}
                >
                  Cancel
                </button>
                <GlassAccentButton type="submit">
                  Add
                </GlassAccentButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEditOpen && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={GLASS_MODAL_PANEL}>
            <h3 className="mb-4 text-center text-lg font-medium">Subscription Details</h3>
            <form onSubmit={handleUpdateSubscription} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={labelCls}>Program</label>
                <input
                  type="text"
                  className={GLASS_FIELD}
                  value={editSubscription.program}
                  onChange={(e) => setEditSubscription(s => ({ ...s, program: e.target.value }))}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Description</label>
                <textarea
                  className={GLASS_FIELD}
                  rows={3}
                  value={editSubscription.description}
                  onChange={(e) => setEditSubscription(s => ({ ...s, description: e.target.value }))}
                />
              </div>
              <div>
                <label className={labelCls}>Amount</label>
                <input
                  type="number"
                  className={GLASS_FIELD}
                  value={editSubscription.amount}
                  onChange={(e) => setEditSubscription(s => ({ ...s, amount: e.target.value }))}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className={labelCls}>Period</label>
                <select
                  className={GLASS_FIELD}
                  value={editSubscription.period}
                  onChange={(e) => setEditSubscription(s => ({ ...s, period: e.target.value }))}
                >
                  <option>Monthly</option>
                  <option>Quarterly</option>
                  <option>Yearly</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Start Date</label>
                <input
                  type="date"
                  className={GLASS_FIELD}
                  value={editSubscription.startDate}
                  onChange={(e) => setEditSubscription(s => ({ ...s, startDate: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>End Date</label>
                <input
                  type="date"
                  className={GLASS_FIELD}
                  value={editSubscription.endDate}
                  onChange={(e) => setEditSubscription(s => ({ ...s, endDate: e.target.value }))}
                />
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select
                  className={GLASS_FIELD}
                  value={editSubscription.status}
                  onChange={(e) => setEditSubscription(s => ({ ...s, status: e.target.value }))}
                >
                  <option value="active">active</option>
                  <option value="upcoming">upcoming</option>
                  <option value="inactive">inactive</option>
                  <option value="phasing out">phasing out</option>
                  <option value="need to kill">need to kill</option>
                </select>
              </div>
              <div className="md:col-span-2 mt-2 flex justify-between gap-3">
                <button
                  type="button"
                  onClick={handleDeleteSubscription}
                  className="rounded-full bg-[#F80707]/10 px-4 py-2 text-sm font-medium text-[#D10606] transition hover:bg-[#F80707]/20 dark:bg-[#F80707]/[0.18] dark:text-[#FF6B5E] dark:hover:bg-[#F80707]/25"
                >
                  Delete
                </button>
                <div className="ml-auto flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsEditOpen(false)}
                    className={GLASS_SECONDARY_BUTTON}
                  >
                    Cancel
                  </button>
                  <GlassAccentButton type="submit">
                    Save
                  </GlassAccentButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </GlassPage>
  );
};

export default TechnologyReports;
